import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeOpenedModal } from '../../redux/actions/headerModal';
import { LOADING_BLUE } from '../../utils/colorCode';
import { defaultImgCDN, imageString } from '../../helper';
import { clearUser, getUser, inviteUser } from '../../redux/actions/user';
import { ROLE_MAP, USER_TYPE_MAP } from '../../utils/mappings';
import LoadingAvatar from '../../components/LoadingAvatar';
import { getBuilding, getOrgBuildingSuites } from '../../redux/actions/building';
import { setToast } from '../../redux/actions/window';
import { getUnit, getUnits } from '../../redux/actions/unit';
import { conformToMask } from 'react-text-mask';
import { phoneNumberMask } from '../../helper/Regx';
import DetailsModalTitleButtons from '../../components/DetailsModalTitleButtons';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS, UserRole, UserType } from './constant';
import { ModalHeader, StyledModal, StyledModalBody } from '../../components/Modals';
import {
  SettingsItem,
  SettingsItemContent,
  SettingsItemSubTitle,
  SettingsItemTitle
} from '../settings/SettingsItem';
import { FaCalendar, FaUser } from 'react-icons/fa';
import { Palette } from '../../components/utils';
import { MdNumbers, MdOutlineEmail, MdOutlinePhoneIphone, MdSecurity } from 'react-icons/md';
import { BsBuildingsFill, BsPostcard } from 'react-icons/bs';
import { HiOutlineUsers } from 'react-icons/hi2';
import { ImageContainer, StyledImage } from '../settings/SettingsImages';
import { getRoomUnitTerminology } from '../../utils/utils';
import moment from 'moment-timezone';
import { useGetConfigs } from '../../hooks/useGetConfigs';
import { requestPending } from '../../utils/status';
import { GET_COMMON_ACCESS, GET_ORG_BUILDING_SUITES, GET_UNITS } from '../../redux/constants';
import styled from 'styled-components';
import { useMemo } from 'react';
import {getCommonAccess} from '../../redux/actions/security';
import {LockProviders} from '../../utils/lockInfo';

const UserDetailModal = () => {
  const { t } = useTranslation();
  const userDetailLabels = {
    name: t('Name'),
    role_type: (hasType) => t(`Role ${hasType ? '/ Type' : ''}`),
    email_address: t('Email address'),
    phone_number: t('Phone number'),
    home_phone_number: t('Home phone number'),
    building: t('Building'),
    move_in: t('Move in'),
    move_out: t('Move out'),
    insurance: t('Insurance'),
    co_residents: t('Co-residents'),
    unit_no: t('Unit number')
  };

  const { modal } = useSelector((state) => state.headerModal);
  const { user, status } = useSelector((state) => state.user);
  const { config } = useGetConfigs({buildingId: user?.building_id});

  const { units: allUnits, status: unitsStatus } = useSelector((state) => state.unit);
  const { allSuites, status: buildingSuitesStatus } = useSelector((state) => state.building);
  const { getCommonAccessData, getCommonAccessStatus } = useSelector((state) => state.security);
  const allBuildings = useSelector((state) => state.building.allBuildings);

  const isUnitsLoading = unitsStatus === requestPending(GET_UNITS);
  const isSuitesLoading = buildingSuitesStatus === requestPending(GET_ORG_BUILDING_SUITES);
  const isDoorsLoading = getCommonAccessStatus === requestPending(GET_COMMON_ACCESS);
  const isLoading = isUnitsLoading || isSuitesLoading || isDoorsLoading;

  const dispatch = useDispatch();
  const [errorImage, setErrorImage] = useState('');

  const additionalBuildings = user?.assigned_buildings?.filter(
    (building) => building !== user?.building_id
  );

  const buildingsMap = useMemo(() => {
    return new Map(allBuildings?.map((building) => [building.id, building.name]));
  }, [allBuildings]);

  const isIsSuite = (user) => {
    return user && user.suite_id > 0;
  };

  useEffect(() => {
    if (user.access) {
      if (user.access.units?.length) {
        dispatch(getUnits({ id: user.building_id }));
      } else if (user.access.suites?.length) {
        dispatch(getOrgBuildingSuites({ buildingId: user.building_id, orgId: user.org_id }));
      }
      if (user.access.doors?.length) {
        dispatch(
          getCommonAccess({
            org_id: user.org_id,
            building_id: user.building_id
          })
        );
      }
    }
  }, [user, dispatch]);

  const onClose = () => {
    setErrorImage('');
    dispatch(clearUser());
    dispatch(
      changeOpenedModal({
        modal: ''
      })
    );
  };

  const showUserDetail = (id) => {
    dispatch(
      getUser({
        id,
        success: () => {
          dispatch(
            changeOpenedModal({
              modal: 'UserDetail'
            })
          );
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to get user!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const showCoResDetails = (id) => {
    dispatch(
      getUser({
        id,
        success: (res) => {
          dispatch(
            changeOpenedModal({
              modal: 'UserDetail',
              data: isIsSuite(res.data)
            })
          );
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to get user!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const handleInvite = (user) => {
    dispatch(
      inviteUser({
        id: user,
        success: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Invitation has been resent successfully!')
            })
          );
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Invitation sending failed!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const handleDetails = (id) => {
    dispatch(
      getUnit({
        id,
        success: () => {
          dispatch(
            changeOpenedModal({
              modal: 'UnitDetail',
              data: config?.suite_enabled
            })
          );
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('failed to get unit data!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  // Helper functions to map IDs to names
  const getUnitNames = (unitIds) => {
    if (!unitIds || !allUnits?.length) return [];
    return unitIds
      .map((id) => {
        const unit = allUnits.find((u) => u.id === id);
        return unit ? unit.unit_num || unit.name : id; // Fallback to ID if name not found
      })
      .filter(Boolean);
  };

  const getSuiteNames = (suiteIds) => {
    if (!suiteIds || !allSuites?.length) return [];
    return suiteIds
      .map((id) => {
        const suite = allSuites.find((s) => s.id === id);
        return suite ? suite.suite_num || suite.name : id; // Fallback to ID if name not found
      })
      .filter(Boolean);
  };

  const getDoorNames = (doorIds) => {
    if (!doorIds || !getCommonAccessData?.length) return [];
    return doorIds
      .map((id) => {
        const door = getCommonAccessData.find((d) => d.id === id);
        return door?.display_name || "";
      })
      .filter(Boolean);
  };

  return modal === 'UserDetail' || modal === 'UserDetailShort' ? (
    <StyledModal
      show={modal === 'UserDetail' || modal === 'UserDetailShort'}
      onClose={onClose}
      closeOnBackdrop={true}>
      <ModalHeader title="User details" />
      {status === 'GET_USER_PENDING' ? (
        <LoadingAvatar color={LOADING_BLUE} />
      ) : (
        <>
          <StyledModalBody>
            <div className="flex justify-content-center w-100">
              <ImageContainer>
                <StyledImage
                  onError={() => setErrorImage(defaultImgCDN)}
                  src={errorImage === '' ? imageString(user.avatar) : errorImage}
                />
              </ImageContainer>
            </div>

            <SettingsItem>
              <FaUser size={24} color={Palette.HAZY_BLUE} />
              <SettingsItemContent>
                <SettingsItemTitle>{t('Name')}</SettingsItemTitle>
                <SettingsItemSubTitle>
                  {user.first_name} {user.last_name}
                </SettingsItemSubTitle>
              </SettingsItemContent>
            </SettingsItem>

            <SettingsItem>
              <BsPostcard size={24} color={Palette.HAZY_BLUE} />
              <SettingsItemContent>
                <SettingsItemTitle>
                  {userDetailLabels.role_type(user.role_id === UserRole.USER)}
                </SettingsItemTitle>
                <SettingsItemSubTitle>
                  {`${ROLE_MAP.find((map) => map.id === user.role_id)?.value} ${
                    user.role_id === UserRole.USER
                      ? `/ ${USER_TYPE_MAP.find((map) => map.id === user.type_id)?.value}`
                      : ''
                  }`}
                </SettingsItemSubTitle>
              </SettingsItemContent>
            </SettingsItem>

            <SettingsItem>
              <MdOutlineEmail size={24} color={Palette.HAZY_BLUE} />
              <SettingsItemContent>
                <SettingsItemTitle>{t('Email address')}</SettingsItemTitle>
                <SettingsItemSubTitle>{user.email}</SettingsItemSubTitle>
              </SettingsItemContent>
            </SettingsItem>

            <SettingsItem>
              <MdOutlinePhoneIphone size={24} color={Palette.HAZY_BLUE} />
              <SettingsItemContent>
                <SettingsItemTitle>{t('Phone number')}</SettingsItemTitle>
                <SettingsItemSubTitle>
                  {user.cell_phone === ''
                    ? '-'
                    : (conformToMask(user.cell_phone, phoneNumberMask) || {}).conformedValue}
                </SettingsItemSubTitle>
              </SettingsItemContent>
            </SettingsItem>

            <SettingsItem>
              <MdOutlinePhoneIphone size={24} color={Palette.HAZY_BLUE} />
              <SettingsItemContent>
                <SettingsItemTitle>{t('Home phone number')}</SettingsItemTitle>
                <SettingsItemSubTitle>
                  {user.home_phone === ''
                    ? '-'
                    : (conformToMask(user.home_phone, phoneNumberMask) || {}).conformedValue}
                </SettingsItemSubTitle>
              </SettingsItemContent>
            </SettingsItem>

            <SettingsItem>
              <BsBuildingsFill size={24} color={Palette.HAZY_BLUE} />
              <SettingsItemContent>
                <SettingsItemTitle>{t('Building')}</SettingsItemTitle>
                <SettingsItemSubTitle
                  link
                  onClick={() => {
                    dispatch(
                      getBuilding({
                        id: user.building_id,
                        success: () => {
                          dispatch(changeOpenedModal({ modal: 'BuildingDetail' }));
                        },
                        fail: () => {
                          dispatch(
                            setToast({
                              toastShow: true,
                              toastMessage: t('Failed to get building data!'),
                              modal: 'errorToast'
                            })
                          );
                        }
                      })
                    );
                  }}>
                  {user.building}
                </SettingsItemSubTitle>
              </SettingsItemContent>
            </SettingsItem>

            {additionalBuildings?.length > 0 && buildingsMap.size > 0 && (
              <SettingsItem>
                <BsBuildingsFill size={24} color={Palette.HAZY_BLUE} />
                <SettingsItemContent>
                  <SettingsItemTitle>{t('Additional Buildings')}</SettingsItemTitle>
                  {additionalBuildings.map((building) => (
                    <SettingsItemSubTitle
                      link
                      onClick={() => {
                        dispatch(
                          getBuilding({
                            id: building,
                            success: () => {
                              dispatch(changeOpenedModal({ modal: 'BuildingDetail' }));
                            },
                            fail: () => {
                              dispatch(
                                setToast({
                                  toastShow: true,
                                  toastMessage: t('Failed to get building data!'),
                                  modal: 'errorToast'
                                })
                              );
                            }
                          })
                        );
                      }}>
                      {buildingsMap.get(building) || ''}
                    </SettingsItemSubTitle>
                  ))}
                </SettingsItemContent>
              </SettingsItem>
            )}

            <SettingsItem>
              <FaCalendar size={24} color={Palette.HAZY_BLUE} />
              <SettingsItemContent>
                <SettingsItemTitle>{t('Move in')}</SettingsItemTitle>
                <SettingsItemSubTitle>
                  {user.move_in === ''
                    ? 'None'
                    : moment(user.move_in).format(DATE_FORMATS.MM_DD_YYYY)}
                </SettingsItemSubTitle>
              </SettingsItemContent>
            </SettingsItem>

            <SettingsItem>
              <FaCalendar size={24} color={Palette.HAZY_BLUE} />
              <SettingsItemContent>
                <SettingsItemTitle>{t('Move out')}</SettingsItemTitle>
                <SettingsItemSubTitle>
                  {user.move_out === ''
                    ? 'None'
                    : moment(user.move_out).format(DATE_FORMATS.MM_DD_YYYY)}
                </SettingsItemSubTitle>
              </SettingsItemContent>
            </SettingsItem>

            {user?.reservations && user.reservations.length > 0 ? (
              <>
                <SettingsItem>
                  <HiOutlineUsers size={24} color={Palette.HAZY_BLUE} />
                  <SettingsItemContent>
                    <SettingsItemTitle>Reservations</SettingsItemTitle>
                    {user.reservations.map((reservation) => (
                      <>
                        <SettingsItemSubTitle
                          key={reservation.id}
                          onClick={() => showUserDetail(reservation.user_id)}>
                          {moment.utc(reservation.check_in_date).format(DATE_FORMATS.MM_DD_YYYY)}-{' '}
                          {moment.utc(reservation.check_out_date).format(DATE_FORMATS.MM_DD_YYYY)}
                        </SettingsItemSubTitle>
                        <SettingsItemSubTitle
                          link
                          cssOverride={'font-size: 16px;'}
                          onClick={() => showUserDetail(reservation.user_id)}>
                          {reservation.user_name}
                        </SettingsItemSubTitle>
                        {reservation.residents.map((resident) => (
                          <SettingsItemSubTitle link onClick={() => showUserDetail(resident.id)}>
                            {resident.name}
                          </SettingsItemSubTitle>
                        ))}
                      </>
                    ))}
                  </SettingsItemContent>
                </SettingsItem>
              </>
            ) : user.type_id === UserType.RESIDENT && user.residents ? (
              <>
                <SettingsItem>
                  <HiOutlineUsers size={24} color={Palette.HAZY_BLUE} />
                  <SettingsItemContent>
                    <SettingsItemTitle>{userDetailLabels.co_residents}</SettingsItemTitle>
                    {user.residents
                      .filter((o) => parseInt(o.type) !== 1)
                      .map((coUsers, index, array) => (
                        <SettingsItemSubTitle
                          key={coUsers.id}
                          link
                          onClick={() => showUserDetail(coUsers.id)}>
                          {coUsers.name}
                          {index < array.length - 1 ? ', ' : ''}
                        </SettingsItemSubTitle>
                      ))}
                  </SettingsItemContent>
                </SettingsItem>
              </>
            ) : null}

            {user.unit_id > 0 && (
              <SettingsItem>
                <MdNumbers size={24} color={Palette.HAZY_BLUE} />
                <SettingsItemContent>
                  <SettingsItemTitle>{getRoomUnitTerminology(config)}</SettingsItemTitle>
                  <SettingsItemSubTitle
                    link={user.unit_id > 0}
                    onClick={() => user.unit_id > 0 && handleDetails(user.unit_id)}>
                    {user.unit_id > 0 ? user.unit_num : 'None'}
                  </SettingsItemSubTitle>
                </SettingsItemContent>
              </SettingsItem>
            )}
            {(user?.role_id === UserRole.VENDOR && ![LockProviders.ICT, LockProviders.ICT_SALTO_SPACE, LockProviders.DORMAKABA].includes(config?.lock_provider)) && (
              <SettingsItem>
                <MdSecurity size={24} color={Palette.HAZY_BLUE} />
                <SettingsItemContent>
                  <SettingsItemTitle>{t('Access Control')}</SettingsItemTitle>
                  {isLoading ? (
                    <LoadingContainer>
                      <LoadingAvatar color={LOADING_BLUE} />
                    </LoadingContainer>
                  ) : (
                    <>
                      {!user?.access?.suites?.length && 
                      !user?.access?.units?.length && 
                      !user?.access?.doors?.length ? (
                        <SettingsItemSubTitle>No access</SettingsItemSubTitle>
                      ) : (
                        <>
                          {user?.access?.suites?.length > 0 && (
                            <SettingsItemSubTitle>
                              <MdNumbers size={24} color={Palette.HAZY_BLUE} />
                              {`Suites: ${getSuiteNames(user.access.suites).join(' | ')}`}
                            </SettingsItemSubTitle>
                          )}

                          {user?.access?.units?.length > 0 && (
                            <SettingsItemSubTitle>
                              <MdNumbers size={24} color={Palette.HAZY_BLUE} />
                              {`Units: ${getUnitNames(user.access.units).join(' | ')}`}
                            </SettingsItemSubTitle>
                          )}

                          {(user?.access?.doors?.length > 0 && 
                            ![
                              LockProviders.ICT, 
                              LockProviders.ICT_SALTO_SPACE, 
                              LockProviders.ICT_ALFRED, 
                              LockProviders.ICT_YALE,
                              LockProviders.ICT_ALLEGION,
                              LockProviders.ICT_SALTO_ALFRED,
                              LockProviders.ICT_SALTO_SLAVE
                            ].includes(config?.lock_provider)) && (
                            <SettingsItemSubTitle>
                              <MdNumbers size={24} color={Palette.HAZY_BLUE} />
                              {`Doors: ${getDoorNames(user.access.doors).join(' | ')}`}
                            </SettingsItemSubTitle>
                          )}
                        </>
                      )}
                    </>
                  )}
                </SettingsItemContent>
              </SettingsItem>
            )}
          </StyledModalBody>
          {config?.org_allow_edits && (
            <DetailsModalTitleButtons
              handleInvite={modal === 'UserDetailShort' ? null : handleInvite}
              modalName="users"
              user={user}
            />
          )}
        </>
      )}
    </StyledModal>
  ) : null;
};

const LoadingContainer = styled.div`
  display: flex;
`;

export default UserDetailModal;
